import React from 'react'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import styled from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import { selectIsCustomerAccessRestricted } from 'containers/App/selectors'

import { RC_PHONE_POINT } from 'components/styles/utils/breakpoints.constants'

const RootContainer = styled.div`
  margin: 30px;
  min-height: calc(100vh - 130px);
  display: flex;
  flex-direction: column;
  border-radius: 10px;

  @media (max-width: ${RC_PHONE_POINT}) {
    margin: 14px;
    min-height: calc(100vh - 98px);
  }

  /* New nav: the shell already provides the page inset, so drop the margin and fill the
     content area below the top bar and global banner. */
  .platform-nav-content & {
    margin: 0;
    min-height: calc(
      100vh - var(--top-bar-height, 52px) - var(--banner-height, 0px) -
        (var(--page-inset, 20px) * 2)
    );
  }
`

const Wrapper = styled.div`
  background-color: var(--ds-blue-8);
  flex: 1;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
`

const Content = styled.div`
  padding: 30px;
  text-align: center;
  line-height: 1.5;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${RC_PHONE_POINT}) {
    padding: 20px 6px;
  }
`

const RestrictedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;

  @media (max-width: ${RC_PHONE_POINT}) {
    max-width: 348px;
    width: 100%;
  }
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
`

const HexagonFrame = styled.div`
  width: 105px;
  height: 105px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--ds-blue-7) 0%, var(--white) 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  box-shadow: 0 4px 20px rgba(137, 167, 255, 0.3);
`

const LogoIcon = styled.img`
  width: 57px;
  height: auto;
`

const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 15px;
  height: 28px;
  background-color: var(--ds-blue-7);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(137, 167, 255, 0.6);
`

const BadgeText = styled.span`
  font-family: 'Figtree', sans-serif;
  font-weight: 550;
  font-size: 12px;
  color: rgba(0, 53, 128, 0.7);
  text-align: center;
`

const Title = styled.h1`
  color: var(--ds-blue-1);
  text-align: center;
  margin: 0;
`

const DotsContainer = styled.div`
  display: flex;
  gap: 10px;
`

const Dot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--ds-blue-3);
`

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 26px;
  background-color: var(--ds-blue-7);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(137, 167, 255, 0.3);
  width: 100%;
  max-width: 348px;

  @media (max-width: ${RC_PHONE_POINT}) {
    max-width: 100%;
  }
`

const InfoText = styled.p`
  color: var(--ds-blue-1);
  text-align: center;
  margin: 0;
  line-height: 26px;
`

const RestrictedAccess = () => {
  const isAccessRestricted = useSelector(selectIsCustomerAccessRestricted)

  if (!isAccessRestricted) {
    return <Redirect to={URL_CONST.dashboard.home} />
  }

  return (
    <RootContainer>
      <Wrapper>
        <Content>
          <RestrictedContainer>
            <LogoContainer>
              <HexagonFrame>
                <LogoIcon
                  src={`${STATIC_BASE_URL}/static/webapp/img/favicon.png`}
                  alt="IRONSCALES"
                />
              </HexagonFrame>
              <Badge>
                <BadgeText>Initial integration period</BadgeText>
              </Badge>
            </LogoContainer>

            <Title className="typography-h4">IRONSCALES is getting to know your environment</Title>

            <DotsContainer>
              <Dot />
              <Dot />
              <Dot />
            </DotsContainer>

            <InfoBox>
              <InfoText className="typography-body-2">
                This brief phase ensures accurate adaptation.
              </InfoText>
              <InfoText className="typography-body-2">
                We&apos;ll notify you once full access is available.
              </InfoText>
            </InfoBox>
          </RestrictedContainer>
        </Content>
      </Wrapper>
    </RootContainer>
  )
}

export default RestrictedAccess
